// ✅ Updated CheckoutPage.js with Chakra UI polish & redirect loading
import React, { useState } from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { useCart } from "../context/CartContext";
import PaystackButton from "../components/PaystackButton";
import { useAuth } from "../hooks/useAuth";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CheckoutPage = () => {
  const { cartItems } = useCart();
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const total = cartItems.reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0);
  const amountInKobo = Math.round(total * 100);

  const publicKey = "pk_live_cf1853432ca0b93e4b472038a979dd2ae0c3d82e";

  const onSuccess = async (ref) => {
    toast({
      title: "Payment successful!",
      description: "Saving your order...",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
    setIsRedirecting(true);

    // Prepare items in the required format
    const items = cartItems.map(item => ({
      mealId: item._id,
      name: item.name,
      quantity: item.quantity,
      unitPrice: item.price,
      totalPrice: item.price * item.quantity,
    }));

    // Prepare order data
    const orderData = {
      firebaseUid: user?.uid,
      items,
      total,
      reference: ref.reference,
      status: 'paid',
      date: new Date(),
    };

    try {
      const res = await fetch(`${API_BASE_URL}/api/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData),
      });
      if (!res.ok) throw new Error('Order save failed');
      setTimeout(() => {
        navigate("/confirmation", {
          state: { order: { reference: ref.reference, total }, paymentType: "Paystack" },
        });
      }, 2000);
    } catch (err) {
      toast({
        title: "Order Save Error",
        description: "Your payment succeeded but we could not save your order. Please contact support.",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
      setIsRedirecting(false);
    }
  };

  const onClose = () => {
    toast({
      title: "Payment cancelled",
      description: "You can try again.",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  };

  if (cartItems.length === 0) {
    return (
      <Box minH="60vh" display="flex" alignItems="center" justifyContent="center" bgGradient="linear(to-br, green.50, white 80%)">
        <Box bg="white" p={10} borderRadius="2xl" boxShadow="2xl" textAlign="center">
          <Heading size="lg" color="green.700">Your cart is empty.</Heading>
          <Text mt={4} color="gray.600">Go back and add some meals.</Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bgGradient="linear(to-br, green.50, white 80%)" py={10} px={2}>
      <Box maxW="600px" mx="auto" bg="white" borderRadius="2xl" boxShadow="2xl" p={{ base: 4, md: 8 }}>
        <Heading mb={8} color="green.700" textAlign="center" fontWeight="extrabold">
          Secure Checkout
        </Heading>
        <VStack spacing={5} align="stretch">
          {cartItems.map((item, idx) => (
            <Box key={idx} p={4} borderWidth={1} borderRadius="xl" bg="gray.50" boxShadow="sm">
              <Text fontWeight="bold" fontSize="lg" color="gray.800">{item.name}</Text>
              <Text color="green.700" fontWeight="semibold" fontSize="md">₦{item.price?.toLocaleString()}</Text>
            </Box>
          ))}

          <Text fontSize="xl" fontWeight="bold" color="gray.700" textAlign="center">
            Total: <Box as="span" color="green.700">₦{Math.round(total).toLocaleString()}</Box>
          </Text>

          <PaystackButton
            publicKey={publicKey}
            email={user?.email || "guest@example.com"}
            amount={amountInKobo}
            onSuccess={onSuccess}
            onClose={onClose}
            disabled={isRedirecting || !publicKey || !user?.email || amountInKobo <= 0}
            style={{ background: '#38a169', color: 'white', border: 'none', padding: '16px', borderRadius: 12, fontWeight: 'bold', width: '100%', fontSize: '1.2rem', boxShadow: '0 2px 6px #0001', opacity: isRedirecting ? 0.7 : 1 }}
          >
            {isRedirecting ? <Spinner size="sm" mr={2} /> : null}
            Pay Now
          </PaystackButton>
        </VStack>
      </Box>
    </Box>
  );
};

export default CheckoutPage;











